import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  CloudQueue,
  Refresh,
  Description,
  Security,
  Folder,
  Assignment
} from '@mui/icons-material';
import api from '../../services/api';
import { useEntityContext } from '../EntityContextSwitch';

// eslint-disable-next-line max-lines-per-function
function FirestoreDetailView() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [firestoreInfo, setFirestoreInfo] = useState(null);
  const { currentEntity } = useEntityContext();

  const clientId = currentEntity?.id;

  const fetchFirestoreInfo = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getEntityFirestoreInfo(clientId);
      setFirestoreInfo(data);
    } catch (err) {
      console.error('Error fetching Firestore info:', err);
      setError(err.message || 'Failed to load Firestore information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchFirestoreInfo();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';

    // Handle Firestore timestamp
    if (dateValue._seconds) {
      return new Date(dateValue._seconds * 1000).toLocaleString();
    }

    // Handle regular date string
    return new Date(dateValue).toLocaleString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!firestoreInfo) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No Firestore information available.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudQueue color="primary" />
            Firestore Data
          </Typography>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchFirestoreInfo} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Card variant="outlined">
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Total Documents
                </Typography>
                <Typography variant="h5">
                  {firestoreInfo.totalDocuments}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Card variant="outlined">
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Total Size
                </Typography>
                <Typography variant="h5">
                  {firestoreInfo.totalSizeFormatted}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Card variant="outlined">
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Total Users
                </Typography>
                <Typography variant="h5">
                  {firestoreInfo.permissions.totalUsers}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Card variant="outlined">
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Collections
                </Typography>
                <Typography variant="h5">
                  {firestoreInfo.subcollections.length + 2}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Grid container spacing={3}>
          {/* Client Document */}
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Description color="primary" />
                  Client Document
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Document ID"
                      secondary={firestoreInfo.clientDocument.documentId}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      secondaryTypographyProps={{ component: 'div' }}
                      primary="Status"
                      secondary={
                        <Chip
                          label={firestoreInfo.clientDocument.status || 'Unknown'}
                          size="small"
                          color={firestoreInfo.clientDocument.status === 'active' ? 'success' : 'default'}
                        />
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Fields"
                      secondary={`${firestoreInfo.clientDocument.fields} fields`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Document Size"
                      secondary={firestoreInfo.clientDocument.documentSizeFormatted}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Created"
                      secondary={formatDate(firestoreInfo.clientDocument.created)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Last Updated"
                      secondary={formatDate(firestoreInfo.clientDocument.lastUpdated)}
                    />
                  </ListItem>
                </List>

                {/* Resources */}
                {firestoreInfo.clientDocument.resources && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Provisioned Resources
                    </Typography>
                    <List dense>
                      {firestoreInfo.clientDocument.resources.bucketName && (
                        <ListItem>
                          <ListItemIcon>
                            <Assignment fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="GCS Bucket"
                            secondary={firestoreInfo.clientDocument.resources.bucketName}
                          />
                        </ListItem>
                      )}
                      {firestoreInfo.clientDocument.resources.datasetId && (
                        <ListItem>
                          <ListItemIcon>
                            <Assignment fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="BigQuery Dataset"
                            secondary={firestoreInfo.clientDocument.resources.datasetId}
                          />
                        </ListItem>
                      )}
                    </List>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Permissions Document */}
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Security color="primary" />
                  Permissions
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Total Users"
                      secondary={firestoreInfo.permissions.totalUsers}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Document Size"
                      secondary={firestoreInfo.permissions.documentSizeFormatted}
                    />
                  </ListItem>
                </List>

                {/* Role Distribution */}
                {Object.keys(firestoreInfo.permissions.roleDistribution).length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Role Distribution
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Role</TableCell>
                            <TableCell align="right">Users</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(firestoreInfo.permissions.roleDistribution).map(([role, count]) => (
                            <TableRow key={role}>
                              <TableCell>{role}</TableCell>
                              <TableCell align="right">{count}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Subcollections */}
          {firestoreInfo.subcollections.length > 0 && (
            <Grid size={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Folder color="primary" />
                    Subcollections
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Collection Name</TableCell>
                          <TableCell align="right">Document Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {firestoreInfo.subcollections.map((collection) => (
                          <TableRow key={collection.name}>
                            <TableCell>{collection.name}</TableCell>
                            <TableCell align="right">{collection.documentCount}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
}

export default FirestoreDetailView;